import { html } from 'lit';
import { measureCallLatency } from '../call-latency.js';

export interface CallDiagnosticLog {
  timestamp?: Date | string | number | null;
  type?: string | null;
  payload?: string | null;
}
export const diagnosticEventTypes = new Set([
  'llm-request',
  'llm-attempt',
  'llm-response',
  'llm-error',
  'llm-recovery',
  'tool-execution',
  'crm-request-start',
  'crm-request-end',
  'crm-contact-response',
  'crm-contact-decision',
  'crm-route-decision',
]);
export const isDiagnosticLog = (log: CallDiagnosticLog) => diagnosticEventTypes.has(String(log.type));
export function diagnosticPayload(log: CallDiagnosticLog): Record<string, any> {
  try {
    return JSON.parse(log.payload ?? '{}');
  } catch {
    return { raw: log.payload };
  }
}
export function diagnosticSummary(log: CallDiagnosticLog) {
  const data = diagnosticPayload(log);
  if (log.type === 'crm-request-start') return `CRM: ${data.toolName ?? '?'} · gestartet`;
  if (log.type === 'crm-request-end')
    return `CRM: ${data.toolName ?? '?'} · ${data.outcome ?? '?'} · ${data.resultCount ?? '—'} Treffer${data.code ? ` · ${data.code}` : ''}`;
  if (log.type === 'crm-contact-response')
    return `CRM-Kontaktantwort: ${data.toolName ?? '?'} · ${data.resultCount ?? '—'} Kontakte${data.truncated ? ' · gekürzt' : ''}`;
  if (log.type === 'crm-contact-decision')
    return `Kontaktsuche: ${data.outcome ?? '?'} · ${data.resultCount ?? '—'} Kontakte`;
  if (log.type === 'crm-route-decision')
    return `Kontaktzuordnung: ${data.routeFamily ?? data.route ?? '?'} · ${data.matchingContactCount ?? 0}/${data.accountContactCount ?? 0} passend · ${data.reason ?? '?'}`;
  if (log.type === 'llm-request')
    return `Verfügbare Tools: ${(data.availableTools ?? []).join(', ') || 'keine'} · ${data.provider ?? '?'} / ${data.model ?? '?'}`;
  if (log.type === 'tool-execution') return `Tool: ${data.tool ?? '?'} · ${data.outcome ?? '?'}`;
  if (log.type === 'llm-error')
    return `Fehler: ${data.category ?? 'unbekannt'}${data.status ? ` (HTTP ${data.status})` : ''}`;
  if (log.type === 'llm-recovery') return `Maßnahme: ${data.action ?? '?'} · ${data.outcome ?? '?'}`;
  if (log.type === 'llm-response')
    return `Modellantwort: ${data.outcome ?? '?'}${data.selectedTools?.length ? ` · ${data.selectedTools.map((t: any) => t.name).join(', ')}` : ''}`;
  return `Versuch ${data.attempt ?? '?'} · ${data.outcome ?? '?'}`;
}
export const formatDiagnosticAsText = (log: CallDiagnosticLog) => `[${log.type}] ${log.payload ?? '{}'}`;
export const renderCallDiagnostic = (log: CallDiagnosticLog) => {
  const data = diagnosticPayload(log);
  return html`<details class="rounded-lg border border-info/25 bg-info/5 p-2.5 text-xs my-1">
    <summary class="cursor-pointer wrap-break-word font-medium">${diagnosticSummary(log)}</summary>
    <div class="mt-2 text-base-content/65 wrap-break-word">
      ${data.requestID ? html`<div>Anfrage: <code>${data.requestID}</code></div>` : ''}
      ${data.generationID ? html`<div>Antwortgenerierung: <code>${data.generationID}</code></div>` : ''}
      ${data.elapsedMs != null ? html`<div>Dauer: ${data.elapsedMs} ms</div>` : ''}
    </div>
    <pre class="mt-2 whitespace-pre-wrap wrap-break-word max-h-96 overflow-auto text-[11px]">
${JSON.stringify(data, null, 2)}</pre
    >
  </details>`;
};

export const renderCallLatency = (logs: CallDiagnosticLog[]) => {
  const values = measureCallLatency(logs);
  const ms = (value: number | null) => (value == null ? 'Nicht erfasst' : `${Math.round(value)} ms`);
  return html`<details class="rounded-lg border border-base-300 p-3 mb-3 text-sm">
    <summary class="cursor-pointer font-semibold">Antwortzeiten aus vorhandenen Protokollen</summary>
    <dl class="grid grid-cols-2 gap-2 mt-3">
      <dt>STT: Sprachende → erste Transkription</dt>
      <dd>${ms(values.sttAfterSpeechMs)}</dd>
      <dt>LLM: erstes Token</dt>
      <dd>${ms(values.llmFirstTokenMs)}</dd>
      <dt>TTS: erster Audio-Chunk</dt>
      <dd>${ms(values.ttsFirstChunkMs)}</dd>
      <dt>Sprachende → erster Audio-Chunk</dt>
      <dd>${ms(values.speechToFirstAudioMs)}</dd>
      <dt>CRM: belegte Zeit (${values.crmRequestsMeasured} Anfragen)</dt>
      <dd>${ms(values.crmBusyMs)}</dd>
    </dl>
    <p class="mt-2 text-base-content/60">
      Jeweils letzte messbare Sprach-/Modellprobe. CRM über die angezeigten Protokolle; parallele Anfragen zählen nicht
      doppelt. Zeiten überlappen und werden nicht addiert. Audio-Chunk bedeutet serverseitige Bereitstellung, nicht
      bestätigte Wiedergabe beim Anrufer.
    </p>
  </details>`;
};
